// @flow
import { connect } from 'react-redux'
import type { Store } from '../../../redux/ducks'
import ReplicatedData, { type Props } from '../../AdEdit/ReplicatedData'
import type { MediaErrors, MediaState } from '../../../redux/ducks/advertisementTemplate/reducers'
import type { Configs, Crop, ImageSizeRestrictions } from '../../types'
import type { ControlVisibility } from '../../AdEdit'
import {
  mediaAdd,
  mediaErrorPatch,
  mediaPatch,
  mediaRemove
} from '../../../redux/ducks/advertisementTemplate/reducers'
import _ from 'lodash'
import store from '../../../redux/store'
import ImageSizeRestrictionsMerger from '../../AdEdit/ImageSizeRestrictionsMerger'

type OwnProps = {|
  action: 'copy' | 'update' | 'create',
  configs: Configs,
  controlVisibility: ControlVisibility,
  allowMultiUpload: boolean,
  replicaIndex: number
|}

function mapStateToProps(state: Store, ownProps: OwnProps) {
  const { replicaIndex, controlVisibility, configs } = ownProps
  const {
    columnImageStoreId,
    multiImageStoreId,
    tmSmallImageStoreId,
    fbInstagramImageStoreId,
    fbStoriesImageStoreId,
    vkColumnImageCrop,
    vkMultiImageCrop,
    vkArticleImageCrop,
    vkSiteImageCrop,
    tmColumnImageCrop,
    tmMultiImageCrop,
    tmSmallImageCrop,
    fbImageCrop,
    fbInstagramImageCrop,
    fbStoriesImageCrop,
    glImageCrop,
    videoStoreId,
    fbThumbnailImageStoreId,
    fbThumbnailImageCrop,
    vkBigImageStoreId
  } = state.advertisementTemplate.media[replicaIndex]
  const {
    columnImageErrors,
    multiImageErrors,
    tmSmallImageErrors,
    fbInstagramImageErrors,
    fbStoriesImageErrors,
    videoErrors,
    fbThumbnailImageErrors,
    vkBigImageErrors
  } = state.advertisementTemplate.mediaErrors[replicaIndex]
  return {
    columnImageStoreId,
    multiImageStoreId,
    tmSmallImageStoreId: tmSmallImageStoreId || multiImageStoreId,
    fbInstagramImageStoreId,
    fbStoriesImageStoreId,
    vkColumnImageCrop,
    vkMultiImageCrop,
    vkArticleImageCrop,
    vkSiteImageCrop,
    tmColumnImageCrop,
    tmMultiImageCrop,
    tmSmallImageCrop,
    fbImageCrop,
    fbInstagramImageCrop,
    fbStoriesImageCrop,
    glImageCrop,
    videoStoreId,
    fbThumbnailImageStoreId,
    fbThumbnailImageCrop,
    vkBigImageStoreId,
    columnImageErrors,
    multiImageErrors,
    tmSmallImageErrors,
    fbInstagramImageErrors,
    fbStoriesImageErrors,
    videoErrors,
    fbThumbnailImageErrors,
    vkBigImageErrors,
    columnImageSizeRestrictions: calculateColumnImageSizeRestrictions(controlVisibility, configs),
    multiImageSizeRestrictions: calculateMultiImageSizeRestrictions(controlVisibility, configs),
    vkBigImageSizeRestrictions: vkBigImageSizeRestrictions(configs),
    isReplica: replicaIndex > 0,
    vkMultiImageConfig: configs.imageConfigCollection.promo.vk,
    tmMultiImageConfig: configs.imageConfigCollection.promo.target_mail,
    fbMultiImageConfig: configs.imageConfigCollection.promo.facebook,
    glMultiImageConfig: configs.imageConfigCollection.promo.google,
    replica: null
  }
}

function getCurrentProps(ownProps: OwnProps) {
  return mapStateToProps(store.getState(), ownProps)
}

export function calculateColumnImageSizeRestrictions(
  visibility: ControlVisibility,
  configs: Configs
): ImageSizeRestrictions {
  const imageConfigs = []
  if (visibility.vkColumnCrop) imageConfigs.push(configs.imageConfigCollection.regular.vk)
  if (visibility.tmColumnCrop) imageConfigs.push(configs.imageConfigCollection.regular.target_mail)

  return new ImageSizeRestrictionsMerger(imageConfigs).restrictions()
}

export function calculateMultiImageSizeRestrictions(
  visibility: ControlVisibility,
  configs: Configs
): ImageSizeRestrictions {
  const imageConfigs = []
  if (visibility.vkMultiCrop || visibility.vkArticleCrop)
    imageConfigs.push(configs.imageConfigCollection.promo.vk)
  if (visibility.vkSiteCrop) imageConfigs.push(configs.imageConfigCollection.site.vk)
  if (visibility.tmMultiCrop) imageConfigs.push(configs.imageConfigCollection.promo.target_mail)
  if (visibility.tmSmallImage) imageConfigs.push(configs.tmSmallImageConfigs)
  if (visibility.fbMultiCrop || visibility.fbThumbnailImage)
    imageConfigs.push(configs.imageConfigCollection.promo.facebook)
  if (visibility.glMultiCrop) imageConfigs.push(configs.imageConfigCollection.promo.google)

  return new ImageSizeRestrictionsMerger(imageConfigs).restrictions()
}

function vkBigImageSizeRestrictions(configs: Configs): ImageSizeRestrictions {
  return new ImageSizeRestrictionsMerger([configs.vkBigImageConfigs]).restrictions()
}

function mapDispatchToProps(dispatch: Function, ownProps: OwnProps) {
  const { replicaIndex } = ownProps

  const changeMedia = (index: number, diff: $Shape<MediaState>) => dispatch(mediaPatch(index, diff))
  const changeErrors = (index: number, diff: $Shape<MediaErrors>) =>
    dispatch(mediaErrorPatch(index, diff))
  const changeCurrentMedia = (diff: $Shape<MediaState>) => dispatch(mediaPatch(replicaIndex, diff))
  const changeCurrentErrors = (diff: $Shape<MediaErrors>) =>
    dispatch(mediaErrorPatch(replicaIndex, diff))
  const addMedia = () => dispatch(mediaAdd())

  return {
    onMediaRemove: () => {
      dispatch(mediaRemove(replicaIndex))
    },
    onColumnImageChange: (storeIds: Array<string>) => {
      _.each(storeIds, (storeId, storeIdIndex: number) => {
        if (storeIdIndex > 0) addMedia() // supposed: multi upload available only when one media
        changeMedia(replicaIndex + storeIdIndex, {
          columnImageStoreId: storeId,
          vkColumnImageCrop: null,
          tmColumnImageCrop: null
        })
        changeErrors(replicaIndex + storeIdIndex, { columnImageErrors: [] })
      })
    },
    onMultiImageChange: (storeIds: Array<string>) => {
      const props = getCurrentProps(ownProps)
      _.each(storeIds, (storeId, storeIdIndex: number) => {
        if (storeIdIndex > 0) addMedia() // supposed: multi upload available only when one media
        if (!props.tmSmallImageStoreId) {
          changeMedia(replicaIndex + storeIdIndex, { tmSmallImageCrop: null })
        }
        changeMedia(replicaIndex + storeIdIndex, {
          multiImageStoreId: storeId,
          vkMultiImageCrop: null,
          vkArticleImageCrop: null,
          vkSiteImageCrop: null,
          tmMultiImageCrop: null,
          fbImageCrop: null,
          glImageCrop: null
        })
        changeErrors(replicaIndex + storeIdIndex, { multiImageErrors: [] })
      })
    },
    onVkBigImageChange: (storeIds: Array<string>) => {
      _.each(storeIds, (storeId, storeIdIndex: number) => {
        if (storeIdIndex > 0) addMedia() // supposed: multi upload available only when one media
        changeMedia(replicaIndex + storeIdIndex, { vkBigImageStoreId: storeId })
        changeErrors(replicaIndex + storeIdIndex, { vkBigImageErrors: [] })
      })
    },
    onVideoChange: (val: string) => {
      changeCurrentMedia({ videoStoreId: val })
      changeCurrentErrors({ videoErrors: [] })
    },
    onFbThumbnailImageChange: (val: string) => {
      changeCurrentMedia({
        fbThumbnailImageStoreId: val,
        fbThumbnailImageCrop: null
      })
      changeCurrentErrors({ fbThumbnailImageErrors: [] })
    },
    onFbInstagramImageChange: (val: string) => {
      changeCurrentMedia({
        fbInstagramImageStoreId: val,
        fbInstagramImageCrop: null
      })
      changeCurrentErrors({ fbInstagramImageErrors: [] })
    },
    onFbStoriesImageChange: (val: string) => {
      changeCurrentMedia({
        fbStoriesImageStoreId: val,
        fbStoriesImageCrop: null
      })
      changeCurrentErrors({ fbStoriesImageErrors: [] })
    },
    onTmSmallImageChange: (val: string) => {
      changeCurrentMedia({
        tmSmallImageStoreId: val,
        tmSmallImageCrop: null
      })
      changeCurrentErrors({ tmSmallImageErrors: [] })
    },
    onFbThumbnailImageCropChange: (crop: Crop) =>
      changeCurrentMedia({ fbThumbnailImageCrop: crop }),
    onVkColumnImageCropChange: (crop: Crop) => changeCurrentMedia({ vkColumnImageCrop: crop }),
    onTmColumnImageCropChange: (crop: Crop) => changeCurrentMedia({ tmColumnImageCrop: crop }),
    onVkMultiImageCropChange: (crop: Crop) => changeCurrentMedia({ vkMultiImageCrop: crop }),
    onVkArticleImageCropChange: (crop: Crop) => changeCurrentMedia({ vkArticleImageCrop: crop }),
    onVkSiteImageCropChange: (crop: Crop) => changeCurrentMedia({ vkSiteImageCrop: crop }),
    onTmMultiImageCropChange: (crop: Crop) => changeCurrentMedia({ tmMultiImageCrop: crop }),
    onTmSmallImageCropChange: (crop: Crop) => changeCurrentMedia({ tmSmallImageCrop: crop }),
    onFbInstagramImageCropChange: (crop: Crop) =>
      changeCurrentMedia({ fbInstagramImageCrop: crop }),
    onFbImageCropChange: (crop: Crop) => changeCurrentMedia({ fbImageCrop: crop }),
    onFbStoriesImageCropChange: (crop: Crop) => changeCurrentMedia({ fbStoriesImageCrop: crop }),
    onGlImageCropChange: (crop: Crop) => changeCurrentMedia({ glImageCrop: crop })
  }
}

const ReplicatedDataContainer = connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(ReplicatedData)

export default ReplicatedDataContainer
